import { X, Plus, Trash2, ArrowUp, ArrowDown, ListChecks } from 'lucide-react';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { categoryCompare } from '../../../lib/categoryOrder';
import type { MenuItem, MenuStructureCategory } from './menuCatalogTypes';

/**
 * La fenêtre d'édition d'une formule, sortie de MenuCatalogPage.tsx qui dépassait la limite de 500 lignes.
 *
 * Comme la fiche plat, elle s'ouvre en fenêtre modale et non sous la liste : le formulaire tombait hors
 * écran dès qu'il y avait quelques formules, et le clic semblait sans effet. Demande d'Emeni (2026-08-25),
 * « pareil pour formule ».
 *
 * Les propriétés portent exactement le nom des variables fermées par le bloc d'origine : son contenu n'a
 * pas été retouché d'une ligne.
 */
export function MenuFormulaForm({
    menuForm, setMenuForm, editingMenuId, items, savingMenu,
    resetMenuForm, saveMenu, addCategoryRow, updateCategoryRow, removeCategoryRow, moveCategoryRow, toggleDishInRow,
}: {
    menuForm: { name: string; structure: MenuStructureCategory[] };
    setMenuForm: (f: { name: string; structure: MenuStructureCategory[] }) => void;
    editingMenuId: string | null;
    items: MenuItem[];
    savingMenu: boolean;
    resetMenuForm: () => void;
    saveMenu: () => void;
    addCategoryRow: () => void;
    updateCategoryRow: (idx: number, patch: Partial<MenuStructureCategory>) => void;
    removeCategoryRow: (idx: number) => void;
    moveCategoryRow: (idx: number, dir: -1 | 1) => void;
    toggleDishInRow: (idx: number, dishId: string) => void;
}) {
    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4" onClick={resetMenuForm}>
            <div className="bg-surface border border-border w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl max-h-[92vh] sm:max-h-[88vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border shrink-0">
                    <h3 className="text-sm font-bold text-content">{editingMenuId ? 'Modifier la formule' : 'Nouvelle formule'}</h3>
                    <button type="button" onClick={resetMenuForm} className="p-1 rounded-lg hover:bg-surface-soft text-content-muted hover:text-content"><X size={16} /></button>
                </div>
                <div className="overflow-y-auto px-4 py-3.5 flex-1 space-y-3">
                            <Input value={menuForm.name} onChange={(e) => setMenuForm({ ...menuForm, name: e.target.value })} placeholder="Nom de la formule (ex: Formule Prestige)" autoFocus />
                            <div className="space-y-2.5">
                                {menuForm.structure.map((row, idx) => (
                                    <div key={idx} className="bg-surface-soft border border-border rounded-lg p-2.5 space-y-2">
                                        <div className="flex items-center gap-1.5">
                                            <Input value={row.category} onChange={(e) => updateCategoryRow(idx, { category: e.target.value })} placeholder="Catégorie (ex: Entrée)" className="flex-1" />
                                            <Button type="button" variant="ghost" size="sm" onClick={() => moveCategoryRow(idx, -1)} disabled={idx === 0} className="px-1.5!"><ArrowUp size={13} /></Button>
                                            <Button type="button" variant="ghost" size="sm" onClick={() => moveCategoryRow(idx, 1)} disabled={idx === menuForm.structure.length - 1} className="px-1.5!"><ArrowDown size={13} /></Button>
                                            <Button type="button" variant="ghost" size="sm" onClick={() => removeCategoryRow(idx)} className="px-1.5! text-red-400! hover:bg-red-500/10! hover:text-red-400!"><Trash2 size={13} /></Button>
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {items.length === 0 && <span className="text-[11px] text-content-muted italic">Ajoute d'abord des plats dans l'onglet "Plats".</span>}
                                            {items.map((i) => (
                                                <button key={i.id} type="button" onClick={() => toggleDishInRow(idx, i.id)}
                                                    className={`px-2.5 py-1 rounded-lg text-[11px] font-bold border transition-all ${row.dish_ids.includes(i.id) ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                                    {i.name}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <button type="button" onClick={addCategoryRow} className="flex items-center gap-1.5 text-xs font-bold text-brand-600 dark:text-brand-400 hover:underline">
                                <ListChecks size={13} /> Ajouter une catégorie
                            </button>
                </div>
                <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-border shrink-0">
                    <Button variant="secondary" size="sm" onClick={resetMenuForm}>Annuler</Button>
                    <Button size="sm" onClick={saveMenu} disabled={savingMenu}>{savingMenu ? 'Enregistrement...' : 'Enregistrer'}</Button>
                </div>
            </div>
        </div>
    );
}
